import React from 'react'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'

export const EDIT_EVENT_SECTIONS = [
  {
    id: 'edit-section-basics',
    label: 'Basics',
    pageTitle: 'Enter details about the event.',
    subtitle: 'The essentials students will see first.',
    fields: ['title', 'tags', 'ageGroup'],
  },
  {
    id: 'edit-section-location',
    label: 'Location',
    pageTitle: 'Where will this event be hosted?',
    subtitle: 'Where students should show up.',
    fields: ['locationId'],
  },
  {
    id: 'edit-section-schedule',
    label: 'Schedule',
    pageTitle: 'When will this event be hosted?',
    subtitle: 'Add one session or a full series.',
    fields: ['sessions'],
  },
  {
    id: 'edit-section-registration',
    label: 'Registration',
    pageTitle: 'How can students register?',
    subtitle: 'Control who can register and when.',
    fields: ['price', 'paymentAccountId', 'maxAttendance', 'accessCode'],
  },
  {
    id: 'edit-section-media',
    label: 'Media & visibility',
    pageTitle: 'How should this event appear?',
    subtitle: 'How the event shows up on PGA.com.',
    fields: ['featuredImageUrl', 'unlisted'],
  },
]

export const getActiveSectionIndex = sectionId =>
  Math.max(
    0,
    EDIT_EVENT_SECTIONS.findIndex(section => section.id === sectionId),
  )

const LEGACY_HASH_TO_SECTION = {
  'payment-account': 'edit-section-registration',
}

export const getSectionIdFromHash = hash => {
  if (!hash || hash.length < 2) {
    return EDIT_EVENT_SECTIONS[0].id
  }

  const sectionId = hash.slice(1)
  const mappedSectionId = LEGACY_HASH_TO_SECTION[sectionId] || sectionId
  const isValidSection = EDIT_EVENT_SECTIONS.some(
    section => section.id === mappedSectionId,
  )

  return isValidSection ? mappedSectionId : EDIT_EVENT_SECTIONS[0].id
}

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      marginBottom: theme.spacing(2),
    },
  },
  stepItem: {
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      flex: 1,
      minWidth: 0,
    },
  },
  stepTrack: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: 18,
    marginRight: theme.spacing(2),
    flexShrink: 0,
    paddingTop: 3,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'row',
      width: '100%',
      marginRight: 0,
      paddingTop: 0,
    },
  },
  circle: {
    width: 16,
    height: 16,
    borderRadius: '50%',
    border: '2.5px solid #587C8C',
    backgroundColor: theme.palette.common.white,
    boxSizing: 'border-box',
    flexShrink: 0,
  },
  circleCompleted: {
    borderColor: '#0053FF',
    backgroundColor: '#0053FF',
  },
  circleActive: {
    borderColor: '#0053FF',
    backgroundColor: '#E8EFFF',
  },
  connector: {
    width: 2,
    height: 36,
    flexShrink: 0,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    backgroundImage:
      'repeating-linear-gradient(to bottom, #587C8C 0, #587C8C 3px, transparent 3px, transparent 6px)',
    backgroundRepeat: 'repeat-y',
    backgroundPosition: 'center top',
    [theme.breakpoints.down('sm')]: {
      flex: 1,
      width: 'auto',
      height: 2,
      margin: theme.spacing(0, 1),
      backgroundImage:
        'repeating-linear-gradient(to right, #587C8C 0, #587C8C 3px, transparent 3px, transparent 6px)',
      backgroundRepeat: 'repeat-x',
      backgroundPosition: 'left center',
    },
  },
  connectorCompleted: {
    backgroundImage:
      'repeating-linear-gradient(to bottom, #0053FF 0, #0053FF 3px, transparent 3px, transparent 6px)',
    [theme.breakpoints.down('sm')]: {
      backgroundImage:
        'repeating-linear-gradient(to right, #0053FF 0, #0053FF 3px, transparent 3px, transparent 6px)',
    },
  },
  stepButton: {
    display: 'block',
    flex: 1,
    minWidth: 0,
    padding: 0,
    paddingBottom: theme.spacing(3.5),
    border: 'none',
    backgroundColor: 'transparent',
    textAlign: 'left',
    cursor: 'pointer',
    fontFamily: 'inherit',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      paddingBottom: 0,
      paddingTop: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
  },
  stepButtonLast: {
    paddingBottom: 0,
  },
  stepLabel: {
    display: 'block',
    fontSize: '1rem',
    fontWeight: 400,
    color: '#172024',
    lineHeight: 1.25,
    marginBottom: theme.spacing(0.25),
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.875rem',
    },
  },
  stepLabelActive: {
    color: '#0053FF',
  },
  stepNumber: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 400,
    color: '#757575',
    lineHeight: 1.25,
  },
}))

const EditEventSectionNav = ({ activeSection, onSectionClick }) => {
  const classes = useStyles()
  const activeIndex = getActiveSectionIndex(activeSection)

  return (
    <Box
      className={classes.root}
      component="nav"
      aria-label="Edit event sections"
    >
      {EDIT_EVENT_SECTIONS.map((section, index) => {
        const isActive = index === activeIndex
        const isCompleted = index < activeIndex
        const isLast = index === EDIT_EVENT_SECTIONS.length - 1
        const stepNumber = index + 1
        const connectorCompleted = index < activeIndex

        return (
          <Box key={section.id} className={classes.stepItem}>
            <Box className={classes.stepTrack} aria-hidden="true">
              <Box
                className={clsx(
                  classes.circle,
                  isCompleted && classes.circleCompleted,
                  isActive && classes.circleActive,
                )}
              />
              {!isLast ? (
                <Box
                  className={clsx(
                    classes.connector,
                    connectorCompleted && classes.connectorCompleted,
                  )}
                />
              ) : null}
            </Box>
            <button
              type="button"
              className={clsx(
                classes.stepButton,
                isLast && classes.stepButtonLast,
              )}
              onClick={() => onSectionClick(section.id)}
              aria-current={isActive ? 'step' : undefined}
            >
              <Typography
                component="span"
                className={clsx(
                  classes.stepLabel,
                  isActive && classes.stepLabelActive,
                )}
              >
                {section.label}
              </Typography>
              <Typography component="span" className={classes.stepNumber}>
                Step {stepNumber}
              </Typography>
            </button>
          </Box>
        )
      })}
    </Box>
  )
}

export default EditEventSectionNav
